import React, { useState, useEffect, useRef } from 'react';
import './ProductsShowcase.css';

const ProductsShowcase = () => {
  const [activeProduct, setActiveProduct] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const sectionRef = useRef(null);

  const products = [
    {
      id: 'mobile-app',
      name: 'ZoneZapp Mobile App',
      tagline: 'Your Community in Your Pocket',
      description: 'Real-time community updates, neighborhood alerts, and local events at your fingertips. Connect with neighbors, discover what\'s happening around you, and stay informed about your community.',
      features: [
        'Real-time neighborhood alerts',
        'Community event discovery',
        'Location-based posts',
        'Direct messaging',
        'Safety notifications'
      ],
      icon: '📱',
      color: '#FF9E7A',
      links: {
        learnMore: '/mobile-app'
      }
    },
    {
      id: 'web-app',
      name: 'ZoneZapp Web Platform',
      tagline: 'Desktop Power for Community Management',
      description: 'Access your community from any browser. Full-featured web experience with enhanced moderation tools, analytics, and community management capabilities.',
      features: [
        'Cross-platform access',
        'Advanced moderation tools',
        'Community analytics',
        'Content management',
        'Multi-zone management'
      ],
      icon: '🌐',
      color: '#5B9FD8',
      links: {
        access: '/webapp/login'
      }
    },
    {
      id: 'crm',
      name: 'ZoneZapp CRM',
      tagline: 'Enterprise Community Management',
      description: 'Professional-grade CRM for businesses and organizations. Manage your community presence, track engagement, create targeted campaigns, and measure impact with powerful analytics.',
      features: [
        'User management',
        'Organization administration',
        'Private zone creation',
        'Mobile user onboarding',
        'Custom email campaigns'
      ],
      icon: '🏢',
      color: '#FFB380',
      links: {
        contact: '/contact-us'
      }
    },
    {
      id: 'hunting-simulator',
      name: 'HuntingZone Simulator',
      tagline: 'Immersive Hunting Experience',
      description: 'Experience the thrill of hunting in a realistic 3D environment. Practice your skills, explore diverse terrains, and enjoy a completely immersive hunting simulation built on ZoneZapp technology.',
      features: [
        'Realistic 3D environments',
        'Multiple hunting scenarios',
        'Wildlife simulation',
        'Weather systems',
        'Skill progression'
      ],
      icon: '🎯',
      color: '#7FC99B',
      links: {
        play: 'https://huntingzone.zonezapp.com'
      }
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const renderProductLinks = (product) => {
    const links = product.links;
    const buttons = [];

    if (links.learnMore) {
      buttons.push(
        <a key="learn-more" href={links.learnMore} className="product-action-btn" style={{ background: product.color }}>
          <i className="fas fa-arrow-right"></i>
          Learn More
        </a>
      );
    } else if (links.ios && links.android) {
      buttons.push(
        <a key="ios" href={links.ios} className="product-action-btn" style={{ background: product.color }}>
          <i className="fab fa-apple"></i>
          Download iOS
        </a>
      );
      buttons.push(
        <a key="android" href={links.android} className="product-action-btn" style={{ background: product.color }}>
          <i className="fab fa-google-play"></i>
          Download Android
        </a>
      );
    } else if (links.access) {
      buttons.push(
        <a key="access" href={links.access} className="product-action-btn" style={{ background: product.color }}>
          <i className="fas fa-sign-in-alt"></i>
          Access Platform
        </a>
      );
    } else if (links.contact) {
      buttons.push(
        <a key="contact" href={links.contact} className="product-action-btn" style={{ background: product.color }}>
          <i className="fas fa-envelope"></i>
          Contact Us
        </a>
      );
    } else if (links.play) {
      buttons.push(
        <a key="play" href={links.play} className="product-action-btn" target="_blank" rel="noopener noreferrer" style={{ background: product.color }}>
          <i className="fas fa-play"></i>
          Play Now
        </a>
      );
    }

    return buttons;
  };

  return (
    <section
      id="products"
      className={`products-showcase ${isVisible ? 'visible' : ''}`}
      ref={sectionRef}
      aria-labelledby="products-showcase-title"
    >
      <div className="products-container">
        {/* Section Header */}
        <div className="products-header">
          <h2 id="products-showcase-title" className="section-title">Our Product Ecosystem</h2>
          <p className="section-subtitle">
            Four integrated solutions, one connected platform. Built for operators, municipalities, enterprises, and community teams.
          </p>
        </div>

        {/* Product Cards Grid */}
        <div className="products-grid">
          {products.map((product, index) => (
            <div
              key={product.id}
              className={`product-card ${activeProduct === index ? 'active' : ''} ${selectedProduct === index ? 'selected' : ''}`}
              role="button"
              tabIndex={0}
              aria-label={`View details for ${product.name}`}
              aria-pressed={selectedProduct === index}
              onMouseEnter={() => setActiveProduct(index)}
              onFocus={() => setActiveProduct(index)}
              onClick={() => setSelectedProduct((prev) => (prev === index ? null : index))}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setSelectedProduct((prev) => (prev === index ? null : index));
                }
              }}
              style={{ '--product-color': product.color }}
            >
              <div className="product-card-inner">
                {/* Front of Card */}
                <div className="product-front">
                  <div className="product-icon-wrapper">
                    <span className="product-icon">{product.icon}</span>
                  </div>
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-tagline">{product.tagline}</p>
                  <div className="hover-indicator">
                    <i className="fas fa-arrow-right"></i>
                    Hover or press Enter to learn more
                  </div>
                </div>

                {/* Back of Card (Appears on Hover) */}
                <div className="product-back">
                  <div className="product-details">
                    <p className="product-description">{product.description}</p>
                    
                    <div className="product-features">
                      <h4>Key Features:</h4>
                      <ul>
                        {product.features.map((feature, idx) => (
                          <li key={idx}>
                            <i className="fas fa-check-circle"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="product-actions">
                      {renderProductLinks(product)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Glow Effect */}
              <div className="product-glow"></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="products-cta">
          <h3>Ready to deploy the ZoneZapp platform?</h3>
          <p>Align your digital channels, simplify operations, and launch a scalable local engagement strategy.</p>
          <a href="/contact-us" className="ecosystem-cta-btn">
            <span>Talk to Our Team</span>
            <i className="fas fa-arrow-right"></i>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProductsShowcase;
